import { useEffect, useRef } from 'react';
import { gsap } from '@/lib/gsap';

interface Options {
  /** Number of slides. */
  count: number;
  /** Currently active slide (controlled from outside, e.g. thumbnails). */
  index: number;
  /** Called when the user swipes to a different slide. */
  onIndexChange: (i: number) => void;
  /** Disable the carousel (e.g. while a video is playing). */
  enabled?: boolean;
}

/**
 * Native-feeling swipe gallery: GSAP Draggable + InertiaPlugin.
 * Drag/swipe carries momentum and snaps to the nearest photo.
 *
 * Both plugins are loaded lazily so they never touch the initial bundle/LCP.
 */
export function useGsapDraggableCarousel({ count, index, onIndexChange, enabled = true }: Options) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  /** True right after a drag, so the click handler doesn't open the lightbox. */
  const wasDraggedRef = useRef(false);
  const indexRef = useRef(index);
  const draggableRef = useRef<any>(null);

  indexRef.current = index;

  useEffect(() => {
    const viewport = viewportRef.current;
    const track = trackRef.current;
    if (!viewport || !track || count < 2 || !enabled) return;
    if (typeof window === 'undefined') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let killed = false;
    let cleanup: (() => void) | undefined;

    (async () => {
      const [{ Draggable }, inertia] = await Promise.all([
        import('gsap/Draggable'),
        import('gsap/InertiaPlugin'),
      ]);
      if (killed) return;
      gsap.registerPlugin(Draggable, (inertia as any).InertiaPlugin ?? (inertia as any).default);

      const slideWidth = () => viewport.getBoundingClientRect().width;

      const [instance] = Draggable.create(track, {
        type: 'x',
        edgeResistance: 0.85,
        dragResistance: 0.05,
        inertia: true,
        allowNativeTouchScrolling: true,
        cursor: 'grab',
        activeCursor: 'grabbing',
        bounds: { minX: -(count - 1) * slideWidth(), maxX: 0 },
        snap: {
          x: (value: number) => {
            const w = slideWidth();
            const snapped = Math.round(value / w) * w;
            return gsap.utils.clamp(-(count - 1) * w, 0, snapped);
          },
        },
        onPress: () => {
          // Fiecare apăsare nouă începe ca „tap"; devine „drag" doar dacă se mișcă.
          wasDraggedRef.current = false;
        },
        onDragStart: () => {
          wasDraggedRef.current = true;
        },
        onThrowComplete: () => {
          const i = Math.round(-instance.x / slideWidth());
          if (i !== indexRef.current) onIndexChange(gsap.utils.clamp(0, count - 1, i));
        },
        onDragEnd: function () {
          if (!(this as any).isThrowing) {
            const i = Math.round(-(this as any).x / slideWidth());
            if (i !== indexRef.current) onIndexChange(gsap.utils.clamp(0, count - 1, i));
          }
        },
      });

      draggableRef.current = instance;
      gsap.set(track, { x: -indexRef.current * slideWidth() });

      const onResize = () => {
        const w = slideWidth();
        instance.applyBounds({ minX: -(count - 1) * w, maxX: 0 });
        gsap.set(track, { x: -indexRef.current * w });
      };
      window.addEventListener('resize', onResize);

      cleanup = () => {
        window.removeEventListener('resize', onResize);
        instance.kill();
        draggableRef.current = null;
      };
    })();

    return () => {
      killed = true;
      cleanup?.();
    };
  }, [count, enabled, onIndexChange]);

  // Keep the track in sync when the slide changes from the outside (thumbnails, arrows).
  useEffect(() => {
    const viewport = viewportRef.current;
    const track = trackRef.current;
    if (!viewport || !track || count < 2) return;
    const w = viewport.getBoundingClientRect().width;
    const targetX = -index * w;
    if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      gsap.set(track, { x: targetX });
      return;
    }
    gsap.to(track, { x: targetX, duration: 0.45, ease: 'power3.out', overwrite: true });
  }, [index, count]);

  return { viewportRef, trackRef, wasDraggedRef };
}
